import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  getSharedTemplateDetailView,
  respondToSharedTemplate,
} from "../utils/Services";
import NoteSectionPending from "../components/NoteSectionPending";
import { ITemplate } from "@/utils/Types";
import { NOTE } from "@/utils/constants";
import TemplateSection from "../components/TemplateSection";
import { useAuthStore } from "@/utils/UseAuthStore";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppSelector } from "@/hooks";
import { isEmpty } from "lodash";
import useIsMobile from "@/hooks/useIsMobile";

const SharedTemplate = () => {
  const [template, setTemplate] = useState({} as ITemplate);
  const [isLoading, setIsLoading] = useState(true);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());
  const user = useAppSelector((state) => state.user.user);
  const isMobile = useIsMobile();

  const { templateId = "", key = "" } = useParams<{
    templateId: string;
    key: string;
  }>();

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const side = searchParams.get("side");

  useEffect(() => {
    const isDarkMode = localStorage.getItem("darkMode") === "true";
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  const fetchTemplate = async (templateId: string, loading = true) => {
    if (loading) setIsLoading(true);
    try {
      const identifier = templateId || key;

      if (!identifier) {
        navigate("/dashboard/shared-templates");
        return;
      }

      const response = await getSharedTemplateDetailView(identifier);

      if (isLoggedIn && user?.id == response?.created_by) {
        navigate(`/dashboard/templates/${response?.id}`);
        return;
      }
      setTemplate(response);
    } catch (error: any) {
      console.error(error);

      if (
        error.response &&
        (error.response.status === 403 || error.response.status === 404)
      ) {
        navigate("/permission-denied");
      }
    }
    if (loading) setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);

    const res = setTimeout(async () => {
      if (!isEmpty(user)) {
        fetchTemplate(templateId);
      }
    }, 500);

    return () => {
      clearTimeout(res);
    };
  }, [templateId, key, user]);

  const handleImportClick = () => {
    setIsImportDialogOpen(true);
  };

  const onImportConfirm = async () => {
    setIsImporting(true);
    try {
      if (isLoggedIn) {
        const res = await respondToSharedTemplate(template?.id!);
        setIsImportDialogOpen(false);
        navigate(`/dashboard/templates/${res?.id}`);
      } else {
        setIsImportDialogOpen(false);
        navigate(`/dashboard/shared-templates`);
      }
    } catch (error: any) {
      console.error("Error importing template:", error);
    } finally {
      setIsImporting(false);
    }
  };

  const onImportCancel = () => {
    setIsImportDialogOpen(false);
  };

  return (
    <div
      className={
        " " +
        (isMobile
          ? "bg-white dark:bg-gray-950"
          : "rounded-lg my-4 mr-4 ml-2 bg-white border-[0.5px] border-gray-25 dark:border-gray-700 shadow-md dark:bg-gray-950")
      }
    >
      <div className="space-x-10 flex h-[56px] py-[8px] px-[24px] border-b-[0.5px] bg-gray-25/20 dark:bg-gray-800/50 border-gray-25 dark:border-gray-600 justify-between">
        {isLoggedIn && (
          <div className="flex items-center cursor-pointer">
            {side && (
              <ChevronLeftIcon
                onClick={() => navigate(`/dashboard/shared-templates`)}
                className="h-[30px] w-[30px] cursor-pointer"
              />
            )}
            <div className="text-gray-600 dark:text-white text-lg font-bold border-gray-300 dark:border-gray-600">
              {template?.title}
            </div>
          </div>
        )}
        <div className="flex items-center ml-auto">
          <Button
            onClick={handleImportClick}
            variant="outline"
            disabled={isLoading}
            className="ml-auto h-[30px] flex items-center gap-2 text-white dark:text-white hover:text-white rounded-full bg-sky-200 border-sky-200 dark:border-sky-200 hover:bg-sky-100"
          >
            <span>Import</span>
          </Button>
        </div>
      </div>
      <div
        className="pt-5"
        style={{
          overflow: "auto",
          height: "calc(100dvh - 157px)",
        }}
      >
        {isLoading ? (
          <>
            {[1, 2, 3, 4].map((index) => (
              <div key={index} className="px-2 py-2 sm:gap-4 sm:px-6">
                <NoteSectionPending />
              </div>
            ))}
          </>
        ) : (
          <>
            {template?.content && (
              <TemplateSection
                key="content"
                title={NOTE["content"]}
                sectionKey="content"
                insertVariable={() => {}}
                showPopup={false}
                variables={[]}
              >
                <div className="text-gray-600 dark:text-white text-base border-gray-300 dark:border-gray-600">
                  {template?.content}
                </div>
              </TemplateSection>
            )}
          </>
        )}
      </div>

      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="sm:max-w-md border-gray-200 dark:border-gray-600 dark:bg-gray-800">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">
              Import Template
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-50">
              <p className="my-2">
                Importing this template will add a copy to your templates
                library.
              </p>
              <p className="font-semibold">
                Are you sure you want to import "{template?.title}"?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onImportCancel}
              className="border-gray-200 dark:border-gray-600 dark:text-white hover:bg-gray-25 dark:hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onImportConfirm}
              disabled={isImporting}
              className="ml-2 text-white dark:text-white hover:text-white rounded-md bg-sky-200 hover:bg-sky-100"
            >
              {isImporting ? "Importing..." : "Confirm Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SharedTemplate;
